import {
  FLOW_TYPES,
  REQUEST_STATUS,
  REQUEST_TYPES,
} from './constants';
import {
  getAccountDisplayName,
  getAccountRoleLabel,
  mapApiFlowToUi,
  mapUiFlowToApi,
} from './approvalChainMappers';
import { getAddressStringByGoogleLocation } from '../job/creation/assets/jobCreationUtill';
import { formateLocalNumber, getCurrencySign } from '../../utils/helper';

export const RECRUITMENT_REQUEST_TIMELINE_RECORD_TYPE = 'RECRUITMENT_REQUEST';

export const REQUEST_API_TYPES = {
  HIRE: 'HIRE_REQUEST',
  CAMPAIGN: 'CAMPAIGN_REQUEST',
};

export const REQUEST_API_ACTION = {
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
};

export const REQUEST_STATUS_IDS = {
  PENDING: 1,
  APPROVED: 14,
  REJECTED: 15,
  WITHDRAWN: 122,
  COMPLETED: 55,
};

const MY_REQUEST_SCOPE = {
  all: 'ALL',
  in_progress: 'IN_PROGRESS',
  approved: 'APPROVED',
  needs_amendment: 'NEEDS_AMENDMENT',
};

const ASSIGNED_REQUEST_SCOPE = {
  awaiting_me: 'AWAITING_ME',
  approved: 'APPROVED',
  rejected: 'REJECTED',
  all: 'ALL',
};

export const mapUiTypeToApi = (type) => {
  if (type === REQUEST_TYPES.CAMPAIGN) return REQUEST_API_TYPES.CAMPAIGN;
  if (type === REQUEST_TYPES.HIRE) return REQUEST_API_TYPES.HIRE;
  return undefined;
};

export const mapApiTypeToUi = (type) => {
  if (type === REQUEST_API_TYPES.CAMPAIGN) return REQUEST_TYPES.CAMPAIGN;
  return REQUEST_TYPES.HIRE;
};

export const mapApiStatusToUi = (statusId) => {
  if (
    statusId === REQUEST_STATUS_IDS.APPROVED ||
    statusId === REQUEST_STATUS_IDS.COMPLETED
  ) {
    return REQUEST_STATUS.APPROVED;
  }
  if (statusId === REQUEST_STATUS_IDS.REJECTED) return REQUEST_STATUS.REJECTED;
  if (statusId === REQUEST_STATUS_IDS.WITHDRAWN) return REQUEST_STATUS.WITHDRAWN;
  return REQUEST_STATUS.PENDING;
};

export const getRecruitmentRequestLinkedJobId = (request) =>
  request?.createdJobId || request?.jobId || request?.job?.id || null;

export const getRecruitmentRequestJhpId = (request) => {
  if (!request) return null;

  const jobHiringPlan = request.jobHiringPlan;
  const nestedPlanId = Array.isArray(jobHiringPlan)
    ? jobHiringPlan[0]?.id
    : jobHiringPlan?.id;

  return (
    request.jhpId ||
    request.jobHiringPlanId ||
    request.createdJhpId ||
    request.linkedJhpId ||
    request.createdJobHiringPlanId ||
    request.requestPayload?.jhpId ||
    request.requestPayload?.jobHiringPlanId ||
    request.job?.jobHiringPlan?.[0]?.id ||
    nestedPlanId ||
    null
  );
};

/** First hiring plan id from GET hiring-plans/job/:jobId/jhp. */
export const resolveJhpIdFromHiringPlansApiResponse = (response) => {
  const plans = response?.hiringPlans ?? response?.options;
  if (Array.isArray(plans) && plans.length > 0) {
    return plans[0]?.id ?? null;
  }
  if (Array.isArray(response) && response.length > 0) {
    return response[0]?.id ?? null;
  }
  return null;
};

/** Kanban applications board for a recruitment request's linked hiring plan. */
export const getRecruitmentRequestKanbanApplicationsPath = (request) => {
  const jhpId = getRecruitmentRequestJhpId(request);
  if (!jhpId) return null;
  return `/jobs/hp-${jhpId}/applications`;
};

/** Campaign details page for a launched campaign request. */
export const getRecruitmentRequestCampaignDetailsPath = (request) => {
  const campaignId = getRecruitmentRequestLinkedCampaignId(request);
  if (!campaignId) return null;
  return `/campaigns/${campaignId}/campaign-details`;
};

/** Job creation wizard path for an approved hire request. */
export const getCreateJobFromRequestPath = (requestId, { templateId } = {}) => {
  if (!requestId) return null;

  const params = new URLSearchParams();
  params.set('recruitmentRequestId', String(requestId));
  if (templateId) {
    params.set('tpl', String(templateId));
  }

  return `/jobs/new?${params.toString()}`;
};

export const getRecruitmentRequestLinkedCampaignId = (request) =>
  request?.createdCampaignId ||
  request?.campaignId ||
  request?.linkedCampaignId ||
  request?.campaign?.id ||
  null;

export const isRecruitmentRequestJobCreated = (request) =>
  request?.statusId === REQUEST_STATUS_IDS.COMPLETED &&
  request?.type !== REQUEST_TYPES.CAMPAIGN &&
  Boolean(getRecruitmentRequestLinkedJobId(request));

/** statusId 55 (COMPLETED) on a campaign request — launch flow finished. */
export const isRecruitmentRequestCampaignLaunched = (request) =>
  request?.type === REQUEST_TYPES.CAMPAIGN &&
  request?.statusId === REQUEST_STATUS_IDS.COMPLETED;

/** Resolve copywrite status from list/detail API shapes (assigned + my-requests). */
export const getRecruitmentRequestCopywriteStatus = (request) =>
  request?.job?.copywriteStatus ??
  request?.job?.copyWriteStatus ??
  request?.copywriteStatus ??
  request?.copyWriteStatus ??
  request?.requestPayload?.job?.copywriteStatus ??
  null;

/** View job only when copywriting is not in progress (null or COMPLETED). */
export const canViewJobFromCopywriteStatus = (request) => {
  const status = getRecruitmentRequestCopywriteStatus(request);
  return status == null || status === '' || String(status).toUpperCase() === 'COMPLETED';
};

const isApproverActioned = (approver) => {
  const actionStatus = approver?.actionStatus ?? approver?.statusId;
  return (
    actionStatus === REQUEST_STATUS_IDS.APPROVED ||
    actionStatus === REQUEST_STATUS_IDS.REJECTED
  );
};

const getActionableApprovers = (request = {}) =>
  (request.approvers || []).filter((a) => a.role !== 'Requester');

export const isRecruitmentRequestFullyApproved = (request) => {
  if (!request) return false;

  const statusId = request.statusId;
  if (statusId != null && statusId !== REQUEST_STATUS_IDS.APPROVED) return false;
  if (statusId == null && request.status !== REQUEST_STATUS.APPROVED) return false;

  const approvers = getActionableApprovers(request);
  const total = approvers.length || request.totalApprovers || 0;
  if (!total) return false;

  const approved =
    request.approvedCount ??
    approvers.filter((a) => a.status === 'approved').length;

  return approved >= total;
};

/** Approved hire request with no linked job yet — eligible for job creation. */
export const canCreateJobFromRecruitmentRequest = (request) => {
  if (!request) return false;
  if (request.type !== REQUEST_TYPES.HIRE) return false;
  if (isRecruitmentRequestJobCreated(request)) return false;
  if (getRecruitmentRequestLinkedJobId(request)) return false;
  return isRecruitmentRequestFullyApproved(request);
};

/** Approved campaign request with job + hiring plan — eligible to launch campaign wizard. */
export const canLaunchCampaignFromRecruitmentRequest = (request) => {
  if (!request) return false;
  if (request.type !== REQUEST_TYPES.CAMPAIGN) return false;
  if (isRecruitmentRequestCampaignLaunched(request)) return false;
  if (getRecruitmentRequestLinkedCampaignId(request)) return false;
  if (!getRecruitmentRequestLinkedJobId(request)) return false;
  if (!getRecruitmentRequestJhpId(request)) return false;
  return isRecruitmentRequestFullyApproved(request);
};

export const getApprovedCampaignActionHelper = (request) => {
  const budget = request?.budget;
  const durationDays = request?.durationDays;
  if (budget && durationDays) {
    return `Spend ${budget} over ${durationDays} days. Locked at approval.`;
  }
  if (budget) return `Spend ${budget}. Locked at approval.`;
  if (durationDays) return `${durationDays} days. Locked at approval.`;
  return undefined;
};

const mapSalaryPeriodToJobForm = (period) => {
  if (!period) return 'year';
  if (period === 'Annum' || period === 'annual') return 'year';
  return period;
};

const isCompetitivePayload = (value) =>
  value === true || value === 1 || value === 'yes' || value === '1';

const buildCentralLocationForJobForm = (request = {}) => {
  const centralLocation = request.centralLocation;
  const locationId =
    centralLocation?.id ??
    centralLocation?.locationId ??
    request.centralLocationId;

  if (!centralLocation && !locationId) return undefined;

  if (centralLocation && typeof centralLocation === 'object') {
    return {
      ...centralLocation,
      id: locationId,
      value: locationId,
      title: centralLocation.title || centralLocation.label || '',
    };
  }

  return {
    id: locationId,
    value: locationId,
    title: typeof request.location === 'string' ? request.location : '',
  };
};

/**
 * Prefill values for /jobs/new from an approved hire recruitment request.
 * JobInformation reads salary, location and job type from jobHiringPlan[0].
 */
export const buildJobCreationPrefillFromRecruitmentRequest = (request = {}) => {
  const departmentRecord = request.departmentRecord;
  const department =
    departmentRecord && typeof departmentRecord === 'object'
      ? {
          value: departmentRecord.id ?? request.departmentId,
          label:
            departmentRecord.title ||
            departmentRecord.name ||
            departmentRecord.label ||
            request.department,
        }
      : request.departmentId
        ? { value: request.departmentId, label: request.department }
        : undefined;

  const centralLocation = buildCentralLocationForJobForm(request);
  const isCompetitive = isCompetitivePayload(request.isCompetitive);
  const salaryPeriod = mapSalaryPeriodToJobForm(request.salaryPeriod);
  const salaryCurrency = request.salaryCurrency || 'GBP';
  const jobType = request.jobType || 'PERMANENT';
  const employmentType = request.employmentType || undefined;

  const hiringPlanEntry = {
    jobType,
    salaryFrom: isCompetitive ? '' : request.salaryFrom ?? '',
    salaryTo: isCompetitive ? '' : request.salaryTo ?? '',
    salaryCurrency,
    salaryPeriod,
    isCompetitive,
    centralLocation,
    budgeted: false,
    remote: request.remote || 'ON_SITE',
    positions: request.positions,
  };

  return {
    title: request.title || request.jobTitle || '',
    department,
    salaryCurrency,
    employmentType,
    recruitmentRequestId: request.id,
    jobHiringPlan: [hiringPlanEntry],
  };
};

const hasAwaitingApprovers = (request = {}) =>
  (request.approvers || []).some((a) => a.status === 'awaiting');

/** Resend notification when pending and at least one approver is awaiting decision. */
export const canResendRecruitmentRequestNotification = (request) => {
  if (!request || request.status !== REQUEST_STATUS.PENDING) return false;

  if (request.canResendNotification != null) {
    return Boolean(request.canResendNotification);
  }

  return hasAwaitingApprovers(request);
};

/** Withdraw allowed only while pending and no approver has approved/rejected yet. */
export const canWithdrawRecruitmentRequest = (request) => {
  if (!request) return false;

  const statusId = request.statusId;
  if (statusId != null) {
    if (statusId !== REQUEST_STATUS_IDS.PENDING) return false;
  } else if (request.status !== REQUEST_STATUS.PENDING) {
    return false;
  }

  return !(request.approvers || []).some(isApproverActioned);
};

const getDepartmentLabel = (payload = {}) => {
  const department = payload.department;
  if (department && typeof department === 'object') {
    return department.title || department.name || department.label || '';
  }
  return payload.departmentName || payload.departmentId || '';
};

const normalizeCentralLocation = (location) => {
  if (!location || typeof location !== 'object') return null;

  return {
    ...location,
    postalTown:
      location.postalTown ||
      location.postaltown ||
      location.town ||
      null,
    postalCode: location.postalCode || location.postcode || null,
  };
};

export const formatRequestLocationLabel = (request = {}) => {
  const centralLocation = normalizeCentralLocation(
    request.centralLocation || request.requestPayload?.centralLocation,
  );

  if (!centralLocation) return '';

  const address = getAddressStringByGoogleLocation(centralLocation, [
    'postalTown',
    'postalCode',
  ]);
  if (address && address !== '-') return address;

  return [centralLocation.postalTown, centralLocation.postalCode]
    .filter(Boolean)
    .join(', ');
};

const getCentralLocationLabel = (payload = {}, request = {}) =>
  formatRequestLocationLabel({
    centralLocation: payload.centralLocation || request.centralLocation,
  });

const formatSalaryRange = (payload = {}) => {
  const { salaryFrom, salaryTo, salaryCurrency, salaryPeriod } = payload;
  if (!salaryFrom && !salaryTo) return '';
  const currency = getCurrencySign(salaryCurrency || 'GBP');
  const period = salaryPeriod ? ` / ${salaryPeriod}` : '';
  if (salaryFrom && salaryTo) {
    return `${currency}${formateLocalNumber(salaryFrom)} - ${currency}${formateLocalNumber(salaryTo)}${period}`.trim();
  }
  if (salaryFrom) return `${currency}${formateLocalNumber(salaryFrom)}${period}`.trim();
  return `${currency}${formateLocalNumber(salaryTo)}${period}`.trim();
};

const toSalaryNumber = (value) => {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const isCompetitiveValue = (value) =>
  value === true || value === 'yes' || value === 1;

const toIsCompetitiveFlag = (value) => (isCompetitiveValue(value) ? 'yes' : 'no');

const getCampaignJobType = (campaign = {}) => {
  const jobSelection = campaign.job;
  const option = jobSelection?.option ?? jobSelection;
  return option?.jobType || option?.job?.jobType || 'PERMANENT';
};

const isApproverPending = (approver) => {
  const actionStatus = approver.actionStatus ?? approver.statusId;
  if (actionStatus === REQUEST_STATUS_IDS.APPROVED) return false;
  if (actionStatus === REQUEST_STATUS_IDS.REJECTED) return false;
  return true;
};

export const computeApproverUiStatus = (approver, allApprovers = [], flow, requestStatusId) => {
  const actionStatus = approver.actionStatus ?? approver.statusId;

  if (actionStatus === REQUEST_STATUS_IDS.APPROVED) return 'approved';
  if (actionStatus === REQUEST_STATUS_IDS.REJECTED) return 'rejected';
  if (requestStatusId === REQUEST_STATUS_IDS.REJECTED) return 'pending';

  const flowType = mapApiFlowToUi(flow);
  const sorted = [...allApprovers].sort((a, b) => (a.seq ?? a.order ?? 0) - (b.seq ?? b.order ?? 0));
  const approverId = approver.accountId ?? approver.userId;

  if (flowType === FLOW_TYPES.PARALLEL) {
    return 'awaiting';
  }

  const firstPending = sorted.find((a) => isApproverPending(a));
  const firstPendingId = firstPending?.accountId ?? firstPending?.userId;

  if (firstPendingId && firstPendingId === approverId) return 'awaiting';
  return 'upcoming';
};

const mapApproverFromApi = (approver, request) => {
  const account = approver?.account || {};
  const accountId = approver.accountId || account.id;
  const name = getAccountDisplayName(account);
  const jobTitle = getAccountRoleLabel(account);

  return {
    id: approver.id,
    userId: accountId,
    accountId,
    name,
    role: jobTitle,
    jobTitle: account.jobTitle,
    email: account.email,
    order: approver.seq,
    seq: approver.seq,
    actionStatus: approver.actionStatus,
    note: approver.note,
    comment: approver.note || approver.comment,
    actedAt:
      approver.actionDate || approver.actedAt || approver.updatedAt || approver.createdAt,
    status: computeApproverUiStatus(approver, request.approvers, request.flow, request.statusId),
  };
};

export const mapRecruitmentRequestFromApi = (request) => {
  if (!request) return null;

  const payload = request.requestPayload || {};
  const type = mapApiTypeToUi(request.requestType);
  const flowType = mapApiFlowToUi(request.flow);
  const centralLocationRaw = payload.centralLocation || request.centralLocation;
  const centralLocation = normalizeCentralLocation(centralLocationRaw);
  const approvers = (request.approvers || []).map((a) => mapApproverFromApi(a, request));
  const approvedCount = approvers.filter((a) => a.status === 'approved').length;
  const account =
    request.account ||
    request.requester ||
    request.requesterAccount ||
    request.createdByAccount ||
    request.createdBy ||
    {};
  const requesterName =
    getAccountDisplayName(account) ||
    request.accountName ||
    request.requesterName ||
    request.createdByName ||
    '';
  const requestedBy = {
    id: account.id || request.accountId || request.requesterId,
    name: requesterName || 'Unknown',
    jobTitle: account.jobTitle,
  };

  const rootJhpId =
    request.jhpId ||
    request.jobHiringPlanId ||
    request.createdJhpId ||
    request.linkedJhpId ||
    request.createdJobHiringPlanId ||
    request.job?.jobHiringPlan?.[0]?.id ||
    (Array.isArray(request.jobHiringPlan)
      ? request.jobHiringPlan[0]?.id
      : request.jobHiringPlan?.id) ||
    null;

  const copywriteStatus = getRecruitmentRequestCopywriteStatus(request);
  const jobWithCopywrite = request.job
    ? { ...request.job, copywriteStatus }
    : copywriteStatus
      ? { copywriteStatus }
      : null;

  const base = {
    id: request.id,
    type,
    requestType: request.requestType,
    status: mapApiStatusToUi(request.statusId),
    statusId: request.statusId,
    flowType,
    flow: request.flow,
    note: request.requestReason,
    reason: request.requestReason,
    justification: request.justification,
    requestedBy,
    createdAt: request.createdAt,
    updatedAt: request.updatedAt,
    approvedCount,
    totalApprovers: approvers.length,
    approvers,
    outOfOfficeNotice: request.outOfOfficeNotice,
    canResendNotification:
      request.canResendNotification ?? request.resendNotificationAllowed,
    createdJobId:
      request.jobId || request.job?.id || request.createdJobId || request.linkedJobId || null,
    jobId: request.jobId || request.job?.id || null,
    job: jobWithCopywrite,
    copywriteStatus,
    jhpId: rootJhpId,
    jobHiringPlanId: rootJhpId,
  };

  if (type === REQUEST_TYPES.CAMPAIGN) {
    const job = payload.job || request.job;
    const jhpId =
      payload.jhpId ||
      payload.jobHiringPlanId ||
      job?.jobHiringPlan?.[0]?.id ||
      request.jhpId ||
      request.jobHiringPlanId;
    return {
      ...base,
      title: payload.requestTitle || payload.title || 'Campaign request',
      jobTitle: job?.title || payload.jobTitle,
      jobId: payload.jobId || job?.id || base.jobId,
      jhpId,
      jobHiringPlanId: jhpId,
      job,
      jobType: payload.jobType || job?.jobType,
      budget: payload.budget,
      durationDays: payload.durationDays,
      channels: payload.channels || payload.channelNames,
      createdCampaignId:
        request.campaignId ||
        request.createdCampaignId ||
        request.linkedCampaignId ||
        payload.campaignId ||
        request.campaign?.id ||
        null,
      campaignId:
        request.campaignId ||
        request.linkedCampaignId ||
        payload.campaignId ||
        request.campaign?.id ||
        null,
    };
  }

  const linkedJob = request.job;
  const jhpId =
    rootJhpId ||
    linkedJob?.jobHiringPlan?.[0]?.id ||
    payload.jhpId ||
    payload.jobHiringPlanId;

  const jobTemplateId =
    payload.jobTemplateId ||
    payload.tplId ||
    payload.jobTemplate?.id ||
    payload.jobTemplate?.value ||
    request.jobTemplateId ||
    request.tplId ||
    request.jobTemplate?.id ||
    request.jobTemplate?.value ||
    null;
  const jobTemplateTitle =
    payload.jobTemplateTitle ||
    payload.jobTemplate?.title ||
    payload.jobTemplate?.label ||
    request.jobTemplate?.title ||
    request.jobTemplate?.label ||
    null;

  return {
    ...base,
    title: payload.title || 'Hire request',
    jobTitle: payload.title || payload.jobTitle,
    jhpId,
    jobHiringPlanId: jhpId,
    department: getDepartmentLabel(payload),
    departmentId: payload.departmentId || payload.department?.id,
    departmentRecord: payload.department,
    location: getCentralLocationLabel(payload, request),
    centralLocationId:
      payload.centralLocationId || centralLocation?.id || centralLocationRaw?.id,
    centralLocation: centralLocation || centralLocationRaw,
    positions: payload.positions,
    salaryRange: formatSalaryRange(payload),
    jobType: payload.jobType,
    employmentType: payload.employmentType,
    jobTemplateId,
    tplId: jobTemplateId,
    jobTemplateTitle,
    jobTemplate: jobTemplateId
      ? {
          value: jobTemplateId,
          label:
            jobTemplateTitle ||
            payload.title ||
            payload.jobTitle ||
            `Template #${jobTemplateId}`,
        }
      : null,
    salaryFrom: payload.salaryFrom,
    salaryTo: payload.salaryTo,
    salaryCurrency: payload.salaryCurrency,
    salaryPeriod: payload.salaryPeriod,
    isCompetitive: payload.isCompetitive,
  };
};

export const mapRecruitmentRequestListParams = ({
  viewType,
  typeFilter = 'all',
  statusTab = 'all',
  page = 1,
  perPage = 50,
  sort = 'createdAt',
  sortDir = 'DESC',
  oooAccId,
} = {}) => {
  const params = { page, perPage, sort, sortDir };

  if (typeFilter && typeFilter !== 'all') {
    params.type = mapUiTypeToApi(typeFilter);
  }

  const scopeMap =
    viewType === 'requester' ? MY_REQUEST_SCOPE : ASSIGNED_REQUEST_SCOPE;
  params.scope = scopeMap[statusTab] || scopeMap.all;

  if (oooAccId != null && oooAccId !== '') {
    params.oooAccId = oooAccId;
  }

  return params;
};

export const buildRecruitmentRequestApiBody = (values) => {
  const { requestType, hire = {}, campaign = {}, approval = {} } = values;
  const isCampaign = requestType === REQUEST_TYPES.CAMPAIGN;
  const flow = mapUiFlowToApi(approval.flowType);
  const approvers = (approval.approvers || [])
    .map((a) => a.value ?? a.userId ?? a.id)
    .filter(Boolean);

  const body = {
    requestType: isCampaign ? REQUEST_API_TYPES.CAMPAIGN : REQUEST_API_TYPES.HIRE,
    flow,
    approvers,
  };

  if (isCampaign) {
    body.requestReason = campaign.reason?.trim() || undefined;
    body.justification = campaign.justification?.trim() || undefined;
    body.requestPayload = {
      jobId: campaign.job?.value ?? campaign.job?.id ?? campaign.liveJobId,
      requestTitle: campaign.title?.trim(),
      jobType: getCampaignJobType(campaign),
    };
  } else {
    body.requestReason = hire.reason?.trim() || undefined;
    body.justification = hire.justification?.trim() || undefined;
    const salaryFrom = toSalaryNumber(hire.salaryFrom);
    const salaryTo = toSalaryNumber(hire.salaryTo);
    const salaryCurrency = hire.salaryCurrency || undefined;
    const isCompetitive = toIsCompetitiveFlag(hire.isCompetitive);

    // Only include jobTemplateId for a real dropdown template selection — never for free-text titles.
    const selectedTemplate = hire.jobTemplate;
    const rawTemplateId =
      selectedTemplate?.value ?? hire.jobTemplateId ?? hire.tplId ?? null;
    const isFreeTextTitle =
      !selectedTemplate ||
      selectedTemplate.__isNew__ === true ||
      selectedTemplate.__isNew === true ||
      rawTemplateId == null ||
      rawTemplateId === '' ||
      String(rawTemplateId) === String(hire.jobTitle || '').trim();
    const jobTemplateId = isFreeTextTitle ? undefined : rawTemplateId;

    body.requestPayload = {
      title: hire.jobTitle?.trim(),
      ...(jobTemplateId != null ? { jobTemplateId } : {}),
      employmentType: hire.employmentType || undefined,
      jobType: hire.jobType || 'PERMANENT',
      centralLocationId:
        hire.location?.id ??
        hire.location?.locationId ??
        hire.location?.value ??
        hire.centralLocationId ??
        undefined,
      positions:
        hire.positions != null && hire.positions !== ''
          ? Number(hire.positions)
          : 1,
      departmentId:
        hire.department?.value ?? hire.department?.id ?? hire.department ?? undefined,
      isCompetitive,
      ...(isCompetitive === 'no'
        ? {
            salaryFrom,
            salaryTo,
            salaryCurrency: salaryCurrency || 'GBP',
            salaryPeriod: hire.salaryPeriod || 'year',
          }
        : {
            salaryPeriod: hire.salaryPeriod || 'year',
          }),
    };
  }

  return body;
};

/** Body for POST /:id/amend-resubmit — payload fields only (no type/flow/approvers). */
export const buildAmendResubmitApiBody = (values) => {
  const { requestPayload, justification, requestReason } = buildRecruitmentRequestApiBody(values);
  return {
    requestPayload,
    ...(justification ? { justification } : {}),
    ...(requestReason ? { requestReason } : {}),
  };
};

/** GET /recruitment-requests/stats/assigned */
export const mapAssignedRecruitmentRequestStatsFromApi = (response = {}) => ({
  awaitingYourApproval: response.stats?.awaitingYourApproval ?? 0,
  pendingHire: response.stats?.pendingHireRequests ?? 0,
  pendingCampaign: response.stats?.pendingCampaignRequests ?? 0,
  approvedThisMonth: response.stats?.approvedByYou30d ?? 0,
});

/** GET /recruitment-requests/stats/my-requests */
export const mapMyRecruitmentRequestStatsFromApi = (response = {}) => ({
  myOpenRequests: response.stats?.myOpenRequests ?? 0,
  myApproved: response.stats?.myApproved ?? 0,
  needsAmendment: response.stats?.needsAmendment ?? 0,
});
